import type { PartialBlock } from '@blocknote/core';

import {
  createBlock,
  deleteBlock,
  reorderBlocks,
  reparentBlock,
  updateBlock,
} from '@/lib/blocks/api';
import { isUuid, newBlockId } from '@/lib/blocks/ids';

import {
  flatBlocksEqual,
  flattenPartialBlocks,
  type FlatBlock,
} from './blocknote-types';

export type SyncSnapshot = {
  flat: FlatBlock[];
  syncVersions: Map<string, number>;
};

export function buildSnapshot(
  pageId: string,
  blocks: PartialBlock[],
  syncVersions: Map<string, number>,
): SyncSnapshot {
  return {
    flat: flattenPartialBlocks(pageId, blocks, syncVersions),
    syncVersions: new Map(syncVersions),
  };
}

function contentPayload(content: FlatBlock['content']): Record<string, unknown> {
  return {
    props: content.props ?? {},
    content: content.content,
  };
}

export async function syncDocument(
  pageId: string,
  blocks: PartialBlock[],
  previous: SyncSnapshot,
): Promise<SyncSnapshot> {
  const next = buildSnapshot(pageId, blocks, previous.syncVersions);
  console.log('[CS-IMG] syncDocument:start', JSON.stringify({
    pageId,
    prevIds: previous.flat.map((b) => ({ id: b.id, type: b.type, parentId: b.parentId })),
    nextIds: next.flat.map((b) => ({ id: b.id, type: b.type, parentId: b.parentId })),
  }));
  const prevById = new Map(previous.flat.map((b) => [b.id, b]));
  const nextById = new Map(next.flat.map((b) => [b.id, b]));
  const syncVersions = new Map(previous.syncVersions);

  for (const block of next.flat) {
    const prev = prevById.get(block.id);
    if (!prev) {
      const id = isUuid(block.id) ? block.id : newBlockId();
      if (block.type === 'image' || block.type === 'file') {
        console.log('[CS-IMG] sync:createBlock', {
          id,
          originalId: block.id,
          parentId: block.parentId,
          type: block.type,
          content: JSON.stringify(contentPayload(block.content)),
        });
      }
      const created = await createBlock({
        id,
        parentId: block.parentId,
        type: block.type,
        content: contentPayload(block.content),
        position: block.position,
      });
      if (block.type === 'image' || block.type === 'file') {
        console.log('[CS-IMG] sync:createBlock:response', {
          id: created.id,
          content: JSON.stringify(created.content),
        });
      }
      syncVersions.set(created.id, created.syncVersion);
      if (id !== block.id) {
        block.id = created.id;
      }
      continue;
    }

    if (!flatBlocksEqual(prev, block)) {
      if (prev.parentId !== block.parentId) {
        const reparented = await reparentBlock(block.id, {
          parentId: block.parentId,
          position: block.position,
        });
        syncVersions.set(reparented.id, reparented.syncVersion);
      }

      const typeChanged = prev.type !== block.type;
      const contentChanged =
        typeChanged ||
        JSON.stringify(prev.content) !== JSON.stringify(block.content);

      if (contentChanged) {
        if (block.type === 'image' || block.type === 'file') {
          console.log('[CS-IMG] sync:updateBlock', {
            id: block.id,
            prevType: prev.type,
            nextType: block.type,
            content: JSON.stringify(contentPayload(block.content)),
          });
        }
        const updated = await updateBlock(block.id, {
          ...(typeChanged ? { type: block.type } : {}),
          content: contentPayload(block.content),
          expectedSyncVersion: syncVersions.get(block.id),
        });
        if (block.type === 'image' || block.type === 'file') {
          console.log('[CS-IMG] sync:updateBlock:response', {
            id: updated.id,
            content: JSON.stringify(updated.content),
          });
        }
        syncVersions.set(updated.id, updated.syncVersion);
      }
    }
  }

  for (const prev of previous.flat) {
    if (!nextById.has(prev.id)) {
      if (prev.type === 'image' || prev.type === 'file') {
        console.warn('[CS-IMG] sync:deleteBlock', {
          id: prev.id,
          type: prev.type,
          content: JSON.stringify(prev.content),
          nextIds: [...nextById.keys()],
        });
      }
      await deleteBlock(prev.id);
      syncVersions.delete(prev.id);
    }
  }

  const positionUpdates: Array<{ id: string; position: number }> = [];
  for (const block of next.flat) {
    const prev = prevById.get(block.id);
    if (prev && prev.position !== block.position) {
      positionUpdates.push({ id: block.id, position: block.position });
    }
  }
  if (positionUpdates.length > 0) {
    const reordered = await reorderBlocks(positionUpdates);
    for (const row of reordered) {
      syncVersions.set(row.id, row.syncVersion);
    }
  }

  return buildSnapshot(pageId, blocks, syncVersions);
}
